"use client"

import { Moon01Icon, Sun01Icon } from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { useRef } from "react"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import { useAnimatedThemeToggle } from "@/lib/use-animated-theme-toggle"
import { cn } from "@/lib/utils"

interface AnimatedThemeTogglerProps
    extends React.ComponentPropsWithoutRef<"button"> {
    duration?: number
    sound?: boolean
}

export const AnimatedThemeToggler = ({
    className,
    duration = 550,
    sound = true,
    ...props
}: AnimatedThemeTogglerProps) => {
    const buttonRef = useRef<HTMLButtonElement>(null)
    const { isDark, mounted, toggleTheme } = useAnimatedThemeToggle(buttonRef, {
        duration,
        sound,
    })

    if (!mounted) {
        return (
            <button className={cn(className)} {...props} disabled>
                <div className="size-5" />
            </button>
        )
    }

    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        aria-keyshortcuts="d"
                        aria-label="Toggle theme (shortcut: d)"
                        ref={buttonRef}
                        onClick={toggleTheme}
                        className={cn(className)}
                        {...props}
                    >
                        {isDark ? (
                            <HugeiconsIcon icon={Sun01Icon} size={20} />
                        ) : (
                            <HugeiconsIcon icon={Moon01Icon} size={20} />
                        )}
                    </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="end">
                    <span className="inline-flex flex-wrap items-center gap-1">
                        Switch theme — press
                        <kbd className="pointer-events-none inline-flex h-5 min-w-5 select-none items-center justify-center rounded border bg-muted px-1 font-medium font-mono text-[0.65rem] text-muted-foreground">
                            d
                        </kbd>
                    </span>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}
